import { useGlobalConfigContext } from 'GlobalConfig.context';
import { useState } from 'preact/hooks';
import { ptpCatchError } from 'services/sentry.services';
import { ErrorCodes } from 'constants/errors.constants';
import { isRuntimeError } from 'utils/fetcher.utils';
import { DropOffCardProps } from '../DropOffCard';

interface IUseDropOffLocationLayout {
  searchDropOffLocationsFn: (
    organizationSlug: string,
    organizationId: number,
    values: { postalCode: string; countryCode: string; carrierId?: string },
  ) => Promise<{ locations: any[] }>;
  carrierId?: string;
}

export const useDropOffLocationLayout = ({
  searchDropOffLocationsFn,
  carrierId,
}: IUseDropOffLocationLayout) => {
  const { organizationId, organizationSlug } = useGlobalConfigContext();
  const [dropOffs, setDropOffs] = useState<DropOffCardProps[]>([]);
  const [center, setCenter] = useState<google.maps.MapOptions['center']>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  // ATM-55: No need to control expanded, so no need to setDropOffs to update expanded
  const onDropOffClick = (dropOffId: string) => {
    const dropOff = dropOffs.find(
      ({ dropOff }) => dropOff.pudoLocationId === dropOffId,
    );

    if (dropOff) {
      const { lat, lng } = dropOff.dropOff;
      setCenter({ lat, lng });
    }
  };

  const onSearchDropOffLocations = async (values: {
    postalCode: string;
    countryCode: string;
  }) => {
    try {
      setIsLoading(true);
      setError(undefined);

      // new ATM-48: update carrierID to the search params when available
      let updateParams = values as {
        postalCode: string;
        countryCode: string;
        carrierId?: string;
      };
      if (carrierId) {
        updateParams = { ...values, carrierId };
      }

      const { locations } = await searchDropOffLocationsFn(
        organizationSlug,
        organizationId,
        updateParams,
      );

      if (locations.length === 0) {
        setDropOffs([]);
        setError('RETURN_TRY_DIFFERENT_POSTAL_CODE');
        return;
      }

      const dropOffs = locations.map((location) => ({
        dropOff: location,
        // ATM-55: No need to set expanded property anymore as it's handled by the Accordion
      }));

      const { lat, lng } = dropOffs[0].dropOff;
      setCenter({ lat, lng });
      setDropOffs(dropOffs);
    } catch (error) {
      if (isRuntimeError(error)) {
        ptpCatchError(error);
        setError(ErrorCodes.GeneralError);
        return;
      }
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    dropOffs,
    center,
    isLoading,
    error,
    onDropOffClick,
    onSearchDropOffLocations,
  };
};
